/**
 * Pricing service - fetches pricing configuration from backend API
 */

import { config } from "@/config/app";
import { formatCurrencyFromCents, getCurrencyConfig } from "@/config/currency";

export interface PricingConfig {
  price_cents: number;
  price_display: string;
  currency: string;
  description: string;
  standard_delivery_minutes?: number;
  priority_delivery?: {
    enabled: boolean;
    fee_cents: number;
    fee_display: string;
    currency: string;
    delivery_minutes: number;
  };
  love_map?: {
    enabled: boolean;
    fee_cents: number;
    fee_display: string;
    currency: string;
    delivery_hours: number;
    forecast_year: number;
  };
}

class PricingService {
  private readonly cache: Map<string, PricingConfig> = new Map(); // Language-aware cache
  private readonly cacheTime: Map<string, number> = new Map();
  private readonly cacheDuration = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cache key including language for separate caching per language
   */
  private getCacheKey(): string {
    return `pricing_${config.i18n.language}`;
  }

  /**
   * Fetch pricing configuration from API
   */
  async getPricingConfig(): Promise<PricingConfig> {
    const now = Date.now();
    const cacheKey = this.getCacheKey();

    // Return cached data if still valid
    if (this.cache.has(cacheKey) && this.cacheTime.has(cacheKey)) {
      const cacheTime = this.cacheTime.get(cacheKey)!;
      if (now - cacheTime < this.cacheDuration) {
        return this.cache.get(cacheKey)!;
      }
    }

    try {
      // Append language query parameter to API call
      const url = new URL(`${config.api.baseUrl}/tarot/pricing`);
      url.searchParams.append("language", config.i18n.language);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const pricing: PricingConfig = await response.json();

      // Guard: o backend faz fallback para o idioma padrão quando não há
      // configuração para o idioma pedido, o que devolveria um preço em outra
      // moeda (ex.: BRL para pt-PT). Exibir esse valor com o símbolo local
      // mostraria um preço errado, então preferimos o fallback do frontend.
      const expectedCurrency = getCurrencyConfig().code.toUpperCase();
      if (
        typeof pricing.currency === "string" &&
        pricing.currency.toUpperCase() !== expectedCurrency
      ) {
        console.error(
          `Pricing currency mismatch: API returned ${pricing.currency} for language ${config.i18n.language}, expected ${expectedCurrency}.`,
        );
        return this.getFallbackPricing();
      }

      // Update language-aware cache
      this.cache.set(cacheKey, pricing);
      this.cacheTime.set(cacheKey, now);

      return pricing;
    } catch (error) {
      console.error("Error fetching pricing config:", error);

      // Return fallback configuration based on current language
      const fallbackPricing = this.getFallbackPricing();

      return fallbackPricing;
    }
  }

  /**
   * Get language-specific fallback pricing
   */
  private getFallbackPricing(): PricingConfig {
    // Language-specific fallback pricing
    if (config.i18n.language === "en-US") {
      return {
        price_cents: 3300,
        price_display: "$33.00",
        currency: "usd",
        description:
          "Channeled Letter: When Tarot translates the voice of the one you love.",
        standard_delivery_minutes: 120,
      };
    }

    if (config.i18n.language === "pt-PT") {
      return {
        price_cents: 1990, // 19,90 €
        price_display: formatCurrencyFromCents(1990, "EUR"),
        currency: "eur",
        description:
          "Carta Canalizada: quando o Tarot traduz a voz de quem tu amas.",
        standard_delivery_minutes: 120,
      };
    }

    // pt-BR default
    return {
      price_cents: 2590, // R$ 25,90
      price_display: "R$ 25,90",
      currency: "brl",
      description:
        "Carta Canalizada: Quando o Tarot traduz a voz de quem você ama.",
      standard_delivery_minutes: 120,
    };
  }

  /**
   * Clear cache to force fresh data on next request
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheTime.clear();
  }

  /**
   * Format price in cents using current currency configuration
   */
  formatPrice(cents: number): string {
    return formatCurrencyFromCents(cents);
  }
}

export const pricingService = new PricingService();
export default pricingService;
